import React, { useState, useEffect } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";
import axiosInstance from "../utils/axiosInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";

// Function to generate random alphanumeric string
const generateCaptcha = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let captcha = "";
  for (let i = 0; i < 6; i++) {
    captcha += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return captcha;
};

const LeadFormModal = ({ show, onClose, pdfUrl, actionType, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    jobTitle: "",
    email: "",
    phoneNumber: "",
  });

  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) {
      setFormData({
        name: "",
        companyName: "",
        jobTitle: "",
        email: "",
        phoneNumber: "",
      });
      setErrors({});
      setCaptchaInput("");
    }
  }, [show]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";
    if (!formData.companyName)
      newErrors.companyName = "Company Name is required";
    if (!formData.jobTitle) newErrors.jobTitle = "Job Title is required";
    if (!formData.phoneNumber) newErrors.phoneNumber = "Phone is required";
    return newErrors;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (type = actionType) => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Captcha validation
    if (captchaInput !== captcha) {
      alert("Captcha is incorrect! Try again.");
      setCaptcha(generateCaptcha()); // regenerate captcha
      setCaptchaInput("");
      return;
    }

    try {
      // Save lead to backend
      //   const response = await fetch("http://localhost:8080/api/leads", {
      //     method: "POST",
      //     headers: { "Content-Type": "application/json" },
      //     body: JSON.stringify({ ...formData, source: type }),
      //   });
      //   if (!response.ok) throw new Error("Failed to save lead");

      const response = await axiosInstance.post("/api/leads/addLead", {
        ...formData,
        source: type,
      });
      if (response.status !== 200) throw new Error("Failed to save lead");

      // Perform action
      if (type === "view") {
        window.open(pdfUrl, "_blank");
      } else if (type === "download") {
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = pdfUrl.split("/").pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (type === "email") {
        // const emailRes = await fetch("http://localhost:8080/api/email/send", {
        //   method: "POST",
        //   headers: { "Content-Type": "application/json" },
        //   body: JSON.stringify({ ...formData, pdfUrl }),
        // });
        // if (!emailRes.ok) throw new Error("Failed to send email");
        // alert("PDF sent to your email successfully!");
        const emailRes = await axiosInstance.post("/api/email/emailPdf", {
          name: formData.name,
          email: formData.email,
          pdfUrl: pdfUrl,
        });

        if (emailRes.status !== 200) throw new Error("Failed to send email");
        alert("PDF sent to your email successfully!");
      }

      onSuccess();
      onClose();
      setCaptcha(generateCaptcha());
      setCaptchaInput("");
    } catch (err) {
      console.error(err);
      alert("Error processing your request.");
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      style={{ "--bs-modal-width": "600px" }}
    >
      <Modal.Header
        closeButton
        className="border-0 pb-0 pt-0 d-flex align-items-center justify-content-between"
        style={{ marginTop: 1 }}
      >
        <Modal.Title className="mb-1">Enter your details</Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        <Form>
          {/* Name */}
          <Form.Group className="mb-1">
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              placeholder="Type your name here"
              value={formData.name}
              onChange={handleChange}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Company Name */}
          <Form.Group className="mb-1">
            <Form.Label>
              Company Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="companyName"
              placeholder="Type your company name here"
              value={formData.companyName}
              onChange={handleChange}
              isInvalid={!!errors.companyName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.companyName}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Job Title */}
          <Form.Group className="mb-1">
            <Form.Label>
              Job Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="jobTitle"
              placeholder="Type your job title here"
              value={formData.jobTitle}
              onChange={handleChange}
              isInvalid={!!errors.jobTitle}
            />
            <Form.Control.Feedback type="invalid">
              {errors.jobTitle}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Email */}
          <Form.Group className="mb-1">
            <Form.Label>
              Email <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              name="email"
              placeholder="Type your email here"
              value={formData.email}
              onChange={handleChange}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Phone */}
          <Form.Group className="mb-1">
            <Form.Label>
              Phone <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="tel"
              name="phoneNumber"
              placeholder="Type your phone here"
              value={formData.phoneNumber}
              onChange={handleChange}
              isInvalid={!!errors.phoneNumber}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phoneNumber}
            </Form.Control.Feedback>
            {/* Captcha */}
            <Form.Group
              className="mb-2 mt-2 d-flex flex-column "
              controlId="formCaptcha"
            >
              <Form.Label className="d-flex flex-column ">Captcha</Form.Label>
              <div
                className="d-flex   align-items-center"
                style={{ gap: "15px" }}
              >
                <Form.Control
                  className="mt-1 text-center"
                  type="text"
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  required
                  placeholder="Enter captcha text"
                  style={{ maxWidth: "200px" }}
                />
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      fontWeight: "bold",
                      // fontSize: "24px",
                      letterSpacing: "4px",
                      background: "#f0f0f0",
                      padding: "7px 16px",
                      borderRadius: "8px",
                      marginRight: "8px",
                      userSelect: "none",
                      display: "inline-block",
                    }}
                  >
                    {captcha}
                  </span>
                  <Button
                    variant="light"
                    size="sm"
                    type="button"
                    onClick={() => {
                      setCaptcha(generateCaptcha());
                      setCaptchaInput("");
                    }}
                    style={{
                      border: "1px solid #ccc",
                      fontSize:"12px",
                      borderRadius: "50%",
                      height: "38px",
                      width: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                  </Button>
                </div>
              </div>
            </Form.Group>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0 d-flex justify-content-end">
        <Button variant="secondary" onClick={onClose} className="me-2">
          Cancel
        </Button>

        {actionType === "download" && (
          <>
            <Button
              variant="primary"
              className="me-2"
              onClick={() => handleSubmit("download")}
              disabled={
                !(
                  formData.name &&
                  formData.companyName &&
                  formData.email &&
                  formData.phoneNumber &&
                  formData.jobTitle &&
                  captchaInput !== ""
                )
              }
            >
              Download
            </Button>
            {/* <Button
              variant="success"
              onClick={() => handleSubmit("email")}
              disabled={
                !(
                  formData.name &&
                  formData.companyName &&
                  formData.email &&
                  formData.phoneNumber &&
                  formData.jobTitle
                )
              }
            >
              Email Presentation
            </Button> */}
          </>
        )}

        {actionType === "view" && (
          <Button
            variant="success"
            onClick={() => handleSubmit("view")}
            disabled={
              !(
                formData.name &&
                formData.companyName &&
                formData.email &&
                formData.phoneNumber &&
                formData.jobTitle &&
                captchaInput !== ""
              )
            }
          >
            View Presentation
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default LeadFormModal;
